import { useEffect, useState } from "react";
import { Form, Select, Button, Skeleton } from "antd";
import { toast } from "react-toastify";
import type { User } from 'types/user';
import { API_URL } from "env";

interface AccountDetail {
    id: string;
    user_id: string;
    account_number: string;
    nickname: string;
    platform: string;
    broker: string;
}

const ReadonlyField = ({ label, value }: { label: string; value: string }) => (
    <div>
        <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-medium border border-border rounded-md py-2 px-3 bg-bg-secondary m-0">{value || '-'}</p>
    </div>
);

const AccountEditModal = ({ account, onClose }: { account: string; onClose: () => void }) => {
    const [form] = Form.useForm();
    const [listUser, setListUser] = useState<User[]>([]);
    const [accountDetail, setAccountDetail] = useState<AccountDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [detailRes, usersRes] = await Promise.all([
                    fetch(`${API_URL}api/v1/be/trade_account/${account}`, {
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    }),
                    fetch(`${API_URL}api/v1/be/users`, {
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    }),
                ]);
                const [detailJson, usersJson] = await Promise.all([detailRes.json(), usersRes.json()]);
                const d = detailJson.data;
                setAccountDetail({ id: d.id, user_id: d.user_id, account_number: d.account_number, nickname: d.nickname, platform: d.platform, broker: d.broker });
                form.setFieldValue('user_id', d.user_id || '');
                setListUser(usersJson.data.items.map((u: any) => ({ id: u.id, name: u.name, email: u.email, avatar_url: u.avatar_url || '' })));
            } catch (err) {
                toast.error('Không thể tải dữ liệu');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [account]);

    const handleSave = async ({ user_id }: { user_id: string }) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}api/v1/be/trade_account/${account}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ user_id: user_id || null }),
            });
            if (!res.ok) throw new Error();
            toast.success('Cập nhật thành công');
            onClose();
        } catch {
            toast.error('Cập nhật thất bại');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Skeleton active paragraph={{ rows: 5 }} />;

    return (
        <div className="space-y-5 py-2">
            {accountDetail && (
                <div className="grid grid-cols-2 gap-4">
                    <ReadonlyField label="Account Number" value={accountDetail.account_number} />
                    <ReadonlyField label="Nickname" value={accountDetail.nickname} />
                    <ReadonlyField label="Platform" value={accountDetail.platform} />
                    <ReadonlyField label="Broker" value={accountDetail.broker} />
                    <ReadonlyField label="Account ID" value={accountDetail.id} />
                </div>
            )}

            <Form form={form} layout="vertical" onFinish={handleSave}>
                <Form.Item name="user_id" label="Assign User">
                    <Select
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Search user..."
                        optionFilterProp="label"
                        options={[
                            { value: '', label: 'Unassign User' },
                            ...listUser.map(u => ({
                                value: u.id,
                                label: `${u.name || ''} ${u.email}`.trim(),
                            })),
                        ]}
                    />
                </Form.Item>

                <div className="flex justify-end gap-2">
                    <Button onClick={onClose}>Hủy</Button>
                    <Button type="primary" htmlType="submit" loading={saving}>Lưu</Button>
                </div>
            </Form>
        </div>
    );
};

export default AccountEditModal;
